/** React.lazy wrapper that cannot crash a route on a nullish module.
 *
 *  Vite's preload helper only rethrows when `vite:preloadError` was NOT
 *  cancelled:
 *
 *      if (!e.defaultPrevented) throw err   // vite/dist/node — preload helper
 *      ...
 *      return baseModule().catch(handlePreloadError)
 *
 *  While errorReporter cancelled that event, a stale chunk therefore made the
 *  import RESOLVE with `undefined`, and a bare `.then(m => ({ default: m.X }))`
 *  dereferenced it — surfacing as a misleading "Cannot read properties of
 *  undefined (reading 'X')" crash on whichever route the user opened
 *  (ERR-197G6AN). errorReporter no longer cancels it, so the import now rejects
 *  properly and ErrorBoundary shows its "new version" screen.
 *
 *  This wrapper stays as the structural guarantee behind that fix: nothing in
 *  React's contract stops a module resolving nullish, and re-adding a
 *  preventDefault() anywhere would silently bring the crash back across all
 *  128 lazy routes. On a nullish module it suspends forever — a reload is
 *  already in flight — so the Suspense fallback holds instead of the tree
 *  tearing down.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** Never settles — parks the Suspense boundary while the page reloads. */
const SUSPEND_FOREVER: Promise<never> = new Promise(() => {});

// Overloads keep the imported module's real type flowing into `pick`, so the
// component's props survive inference. Typing `load` as `Promise<any>` instead
// collapses every lazy component's props to `{}` and every call site starts
// failing with "not assignable to type 'IntrinsicAttributes'".
export function lazyChunk<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T>;
export function lazyChunk<M, T extends ComponentType<any>>(
  load: () => Promise<M>,
  pick: (module: M) => T,
): LazyExoticComponent<T>;
export function lazyChunk(
  load: () => Promise<any>,
  pick?: (module: any) => ComponentType<any>,
): LazyExoticComponent<ComponentType<any>> {
  return lazy(async () => {
    const module = await load();
    if (module == null) {
      // The only path that resolves nullish is the cancelled preload error
      // above, so a reload is already scheduled. Warn anyway: if that ever
      // stops being true this is the sole breadcrumb before a stuck spinner.
      console.warn('[lazyChunk] stale chunk resolved to undefined — awaiting reload');
      return SUSPEND_FOREVER;
    }
    // A missing named export is a genuine bug, not a stale chunk — let React
    // raise its own "Element type is invalid" rather than masking it here.
    return { default: pick ? pick(module) : module.default };
  });
}
